import { Icon } from "../Common.jsx";
import { fmtDate } from "../../lib/dates.js";
import { formatMoney } from "../../lib/money.js";

const STATUS_LABEL = {
  paid: "Paid",
  unpaid: "Unpaid",
  voided: "Voided",
  partially_refunded: "Partially Refunded",
  refunded: "Refunded",
};

// Owner-facing invoice document. Unlike the admin modal this takes the
// invoice + company straight from props (the owner's invoice list already
// carries every field), so there's no extra fetch / loading state.
export default function InvoiceDetailModal({ invoice, company, onClose }) {
  const data = invoice;
  const currency = data?.currency || "INR";
  const refundedAmount = data?.refundedAmount ?? 0;
  const subTotal = data?.amount || 0;
  const totalPaid = refundedAmount > 0 ? subTotal - refundedAmount : subTotal;
  // A short, human-friendly invoice number derived from the row id.
  const invNo = data?.id ? `INV-${data.id.slice(-8).toUpperCase()}` : "—";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head><title>${invNo}</title>
      <style>
        @page { margin: 15mm; }
        body {
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          background: #fff;
          color: #1a1a1a;
          margin: 0;
          padding: 40px;
          font-size: 13px;
          line-height: 1.5;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 24px; border-bottom: 2px solid #1a1a1a; }
        .inv-brand-name { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; }
        .inv-brand-sub { font-size: 11px; color: #666; }
        .inv-title { font-size: 28px; font-weight: 300; text-align: right; letter-spacing: 3px; margin: 0; }
        .inv-meta { text-align: right; font-size: 12px; color: #666; margin-top: 4px; }
        .inv-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 20px; }
        .inv-badge-paid { background: #d1fae5; color: #065f46; }
        .inv-badge-unpaid { background: #fef3c7; color: #92400e; }
        .inv-badge-voided { background: #f3f4f6; color: #6b7280; }
        .inv-badge-other { background: #dbeafe; color: #1e40af; }
        .inv-info { display: flex; justify-content: space-between; margin-bottom: 32px; }
        .inv-box-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 4px; }
        .inv-box-value { font-size: 14px; font-weight: 600; }
        .inv-box-sub { font-size: 12px; color: #666; margin-top: 2px; }
        .inv-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        .inv-table th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #999; padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
        .inv-table th:last-child { text-align: right; }
        .inv-table td { padding: 14px 12px; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
        .inv-table td:last-child { text-align: right; font-weight: 600; }
        .inv-total-wrap { margin-left: auto; width: 280px; }
        .inv-total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
        .inv-total-row.grand { border-top: 2px solid #1a1a1a; padding-top: 10px; margin-top: 4px; font-weight: 700; font-size: 16px; }
        .inv-total-label { color: #666; }
        .inv-footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #999; text-align: center; }
      </style></head>
      <body>
        <div class="inv-header">
          <div>
            <div class="inv-brand-name">Bizzfly</div>
            <div class="inv-brand-sub">Daily Reports Platform</div>
          </div>
          <div>
            <h1 class="inv-title">INVOICE</h1>
            <div class="inv-meta">${invNo}</div>
          </div>
        </div>

        <span class="inv-badge ${
          data.status === "paid" ? "inv-badge-paid"
          : data.status === "unpaid" ? "inv-badge-unpaid"
          : data.status === "voided" ? "inv-badge-voided"
          : "inv-badge-other"
        }">${STATUS_LABEL[data.status] || data.status}</span>

        <div class="inv-info">
          <div>
            <div class="inv-box-label">Bill To</div>
            <div class="inv-box-value">${company?.name || "—"}</div>
            <div class="inv-box-sub">${company?.slug || ""}</div>
          </div>
          <div style="text-align:right">
            <div class="inv-box-label">Invoice Details</div>
            <div class="inv-box-value" style="font-weight:500">Date: ${fmtDate(data.createdAt)}</div>
            <div class="inv-box-sub">Period: ${fmtDate(data.periodStart)} — ${fmtDate(data.periodEnd)}</div>
            ${data.razorpayInvoiceId ? `<div class="inv-box-sub">Ref: ${data.razorpayInvoiceId}</div>` : ""}
          </div>
        </div>

        <table class="inv-table">
          <thead>
            <tr><th style="width:75%">Description</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Subscription — ${fmtDate(data.periodStart)} to ${fmtDate(data.periodEnd)}</td>
              <td>${formatMoney(subTotal, currency)}</td>
            </tr>
          </tbody>
        </table>

        <div class="inv-total-wrap">
          <div class="inv-total-row">
            <span class="inv-total-label">Subtotal</span>
            <span>${formatMoney(subTotal, currency)}</span>
          </div>
          ${refundedAmount > 0 ? `
          <div class="inv-total-row">
            <span class="inv-total-label">Refunded</span>
            <span style="color:#dc2626">-${formatMoney(refundedAmount, currency)}</span>
          </div>` : ""}
          <div class="inv-total-row grand">
            <span>Total</span>
            <span>${formatMoney(totalPaid, currency)}</span>
          </div>
        </div>

        <div class="inv-footer">
          Bizzfly &middot; Daily Reports Platform &middot; ${invNo}
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const labelStyle = { fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#999", marginBottom: 4 };

  return (
    <>
      <div className="modal-head">
        <h3><Icon name="receipt" size={16} /> Invoice</h3>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" onClick={handlePrint}>
            <Icon name="printer" size={14} /> Print
          </button>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
      </div>

      <div className="modal-body" style={{ padding: 0 }}>
        <div style={{
          background: "#fff",
          color: "#1a1a1a",
          fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
          fontSize: 13,
          lineHeight: 1.5,
        }}>
          {/* Header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "32px 32px 20px", borderBottom: "2px solid #1a1a1a", marginBottom: 24,
          }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px" }}>Bizzfly</div>
              <div style={{ fontSize: 11, color: "#666" }}>Daily Reports Platform</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 28, fontWeight: 300, letterSpacing: 3, margin: 0 }}>INVOICE</div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4, fontFamily: "monospace" }}>{invNo}</div>
            </div>
          </div>

          {/* Status */}
          <div style={{ padding: "0 32px", marginBottom: 20 }}>
            <span style={{
              display: "inline-block", padding: "3px 12px", borderRadius: 20,
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1,
              background: data.status === "paid" ? "#d1fae5"
                : data.status === "unpaid" ? "#fef3c7"
                : data.status === "voided" ? "#f3f4f6" : "#dbeafe",
              color: data.status === "paid" ? "#065f46"
                : data.status === "unpaid" ? "#92400e"
                : data.status === "voided" ? "#6b7280" : "#1e40af",
            }}>
              {STATUS_LABEL[data.status] || data.status}
            </span>
          </div>

          {/* Two-column info */}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "0 32px", marginBottom: 28 }}>
            <div>
              <div style={labelStyle}>Bill To</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{company?.name || "—"}</div>
              {company?.slug && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{company.slug}</div>}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={labelStyle}>Invoice Details</div>
              <div style={{ fontSize: 13 }}>Date: {fmtDate(data.createdAt)}</div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                Period: {fmtDate(data.periodStart)} — {fmtDate(data.periodEnd)}
              </div>
              {data.razorpayInvoiceId && (
                <div style={{ fontSize: 11, color: "#999", marginTop: 2, fontFamily: "monospace" }}>
                  Ref: {data.razorpayInvoiceId}
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <div style={{ padding: "0 32px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#999", padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}>Description</th>
                  <th style={{ textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#999", padding: "10px 12px", borderBottom: "1px solid #e5e7eb", width: 140 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "14px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 13, fontWeight: 500 }}>
                    Subscription — {fmtDate(data.periodStart)} to {fmtDate(data.periodEnd)}
                  </td>
                  <td style={{ padding: "14px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                    {formatMoney(subTotal, currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ padding: "0 32px", marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ width: 260 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                <span style={{ color: "#666" }}>Subtotal</span>
                <span style={{ fontWeight: 600 }}>{formatMoney(subTotal, currency)}</span>
              </div>
              {refundedAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                  <span style={{ color: "#666" }}>Refunded</span>
                  <span style={{ color: "#dc2626", fontWeight: 600 }}>-{formatMoney(refundedAmount, currency)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", marginTop: 4, borderTop: "2px solid #1a1a1a", fontSize: 16, fontWeight: 700 }}>
                <span>Total</span>
                <span>{formatMoney(totalPaid, currency)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: 40, padding: "16px 32px 24px", borderTop: "1px solid #e5e7eb", fontSize: 10, color: "#999", textAlign: "center" }}>
            Bizzfly &middot; Daily Reports Platform &middot; {invNo}
          </div>
        </div>
      </div>

      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={handlePrint}>
          <Icon name="printer" size={14} /> Print Invoice
        </button>
      </div>
    </>
  );
}
